// Import from enhanced API (when ready to use high-performance features)
// export * from './api-enhanced';

import { API_BASE_URL } from './apiConfig';

// Demo mode - works without backend
const DEMO_MODE = import.meta.env.VITE_DEMO_MODE === 'true';

// Token management
const getToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('auth_token') || localStorage.getItem('token');
};

const getRefreshToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('refresh_token');
};

const setToken = (token: string): void => {
  if (typeof window !== 'undefined') {
    localStorage.setItem('auth_token', token);
    localStorage.setItem('token', token);
  }
};

const setRefreshToken = (token: string): void => {
  if (typeof window !== 'undefined') {
    localStorage.setItem('refresh_token', token);
  }
};

const removeToken = (): void => {
  if (typeof window !== 'undefined') {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('currentUser');
  }
};

// API Client with automatic token injection
class ApiClient {
  private baseURL: string;
  private refreshingTokenPromise: Promise<string | null> | null = null;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  private async parseResponse(response: Response): Promise<any> {
    const raw = await response.text();
    if (!raw) return {};
    try {
      return JSON.parse(raw);
    } catch {
      return { message: raw };
    }
  }

  private shouldRetryWithVersionedApi(response: Response, endpoint: string): boolean {
    return (
      response.status === 404 &&
      !this.baseURL.includes('/api/v1') &&
      this.baseURL.endsWith('/api') &&
      !endpoint.startsWith('/v1/')
    );
  }

  private shouldRetryWithoutVersionedApi(response: Response): boolean {
    return response.status === 404 && this.baseURL.endsWith('/api/v1');
  }

  private makeRequestUrl(
    endpoint: string,
    options: { useVersionedApi?: boolean; useUnversionedApi?: boolean } = {}
  ): string {
    if (options.useUnversionedApi) {
      const unversionedBase = this.baseURL.replace(/\/api\/v1$/, '/api');
      return `${unversionedBase}${endpoint}`;
    }
    if (options.useVersionedApi) {
      return `${this.baseURL}/v1${endpoint}`;
    }
    return `${this.baseURL}${endpoint}`;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = getToken();
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (options.headers) {
      Object.assign(headers, options.headers as Record<string, string>);
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      let response = await fetch(this.makeRequestUrl(endpoint), {
        ...options,
        headers,
      });

      if (this.shouldRetryWithVersionedApi(response, endpoint)) {
        response = await fetch(this.makeRequestUrl(endpoint, { useVersionedApi: true }), {
          ...options,
          headers,
        });
      } else if (this.shouldRetryWithoutVersionedApi(response)) {
        response = await fetch(this.makeRequestUrl(endpoint, { useUnversionedApi: true }), {
          ...options,
          headers,
        });
      }

      const data = await this.parseResponse(response);

      const isTokenExpired =
        response.status === 401 &&
        typeof data?.message === 'string' &&
        data.message.toLowerCase().includes('token expired');

      const isAuthEndpoint =
        endpoint.includes('/auth/login') ||
        endpoint.includes('/auth/register') ||
        endpoint.includes('/auth/refresh-token');

      const retried = (options as any).__retried === true;

      if (isTokenExpired && !isAuthEndpoint && !retried) {
        const newToken = await this.refreshAccessToken();
        if (newToken) {
          const retryOptions: RequestInit = {
            ...options,
            headers: {
              ...(options.headers as Record<string, string> || {}),
              Authorization: `Bearer ${newToken}`,
            },
          };
          (retryOptions as any).__retried = true;
          return this.request<T>(endpoint, retryOptions);
        }
        removeToken();
      }

      if (!response.ok) {
        const details = data?.errors?.length
          ? ` | ${JSON.stringify(data.errors)}`
          : '';
        const errorMessage = `[${response.status}] ${data?.message || 'API request failed'}${details}`;
        throw new Error(errorMessage);
      }

      return data;
    } catch (error) {
      // Silent error - let the calling code handle it
      throw error;
    }
  }

  private async refreshAccessToken(): Promise<string | null> {
    if (this.refreshingTokenPromise) {
      return this.refreshingTokenPromise;
    }

    const refreshToken = getRefreshToken();
    if (!refreshToken) return null;

    this.refreshingTokenPromise = (async () => {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      const body = JSON.stringify({ refreshToken });

      let response = await fetch(this.makeRequestUrl('/auth/refresh-token'), {
        method: 'POST',
        headers,
        body,
      });

      if (this.shouldRetryWithoutVersionedApi(response)) {
        response = await fetch(this.makeRequestUrl('/auth/refresh-token', { useUnversionedApi: true }), {
          method: 'POST',
          headers,
          body,
        });
      } else if (this.shouldRetryWithVersionedApi(response, '/auth/refresh-token')) {
        response = await fetch(this.makeRequestUrl('/auth/refresh-token', { useVersionedApi: true }), {
          method: 'POST',
          headers,
          body,
        });
      }

      const data = await this.parseResponse(response);
      if (!response.ok || !data?.success || !data?.data?.token) {
        return null;
      }

      setToken(data.data.token);
      return data.data.token as string;
    })();

    try {
      return await this.refreshingTokenPromise;
    } finally {
      this.refreshingTokenPromise = null;
    }
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, body?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async put<T>(endpoint: string, body?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async patch<T>(endpoint: string, body?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

// Create API client instance
const apiClient = new ApiClient(API_BASE_URL);

const cleanParams = (params?: Record<string, any>) => {
  if (!params) return '';
  const entries = Object.entries(params).filter(([, value]) => {
    if (value === undefined || value === null) return false;
    if (typeof value === 'string' && value.trim() === '') return false;
    return true;
  });
  return new URLSearchParams(entries as [string, string][]).toString();
};

const mapRestaurantPayloadForApi = (restaurantData: any = {}) => {
  return {
    integrationSetup: restaurantData.integrationSetup ?? restaurantData.integration_setup,
    name: restaurantData.name,
    public_menu_enabled:
      restaurantData.public_menu_enabled ??
      restaurantData.publicMenuEnabled ??
      restaurantData.publicMenu ??
      restaurantData.public_menu,
    public_menu_key:
      restaurantData.public_menu_key ??
      restaurantData.publicMenuKey,
    description: restaurantData.description,
    logo: restaurantData.logo,
    banner: restaurantData.banner,
    coverImage: restaurantData.coverImage ?? restaurantData.cover_image,
    cuisineType: restaurantData.cuisineType ?? restaurantData.cuisine_type,
    address: restaurantData.address,
    cityId: restaurantData.cityId ?? restaurantData.city_id,
    stateId: restaurantData.stateId ?? restaurantData.state_id,
    countryId: restaurantData.countryId ?? restaurantData.country_id,
    city: restaurantData.city,
    state: restaurantData.state,
    pincode: restaurantData.pincode ?? restaurantData.zipCode,
    latitude: restaurantData.latitude,
    longitude: restaurantData.longitude,
    phone: restaurantData.phone,
    email: restaurantData.email,
    website: restaurantData.website,
    primaryColor: restaurantData.primaryColor ?? restaurantData.primary_color,
    secondaryColor: restaurantData.secondaryColor ?? restaurantData.secondary_color,
    openTime: restaurantData.openTime ?? restaurantData.open_time,
    closeTime: restaurantData.closeTime ?? restaurantData.close_time,
    deliveryTime: restaurantData.deliveryTime ?? restaurantData.delivery_time,
    minimumOrder: restaurantData.minimumOrder ?? restaurantData.minimum_order,
    deliveryFee: restaurantData.deliveryFee ?? restaurantData.delivery_fee,
    freeDeliveryAbove: restaurantData.freeDeliveryAbove ?? restaurantData.free_delivery_above,
    taxRate: restaurantData.taxRate ?? restaurantData.tax_rate,
    packagingCharges: restaurantData.packagingCharges ?? restaurantData.packaging_charges,
    isPureVeg: restaurantData.isPureVeg ?? restaurantData.is_pure_veg,
    amenities: restaurantData.amenities,
    isActive: restaurantData.isActive ?? restaurantData.is_active,
    ownerId: restaurantData.ownerId ?? restaurantData.owner_id,
    priceRangeId: restaurantData.priceRangeId ?? restaurantData.priceRange,
    foodTypeIds: restaurantData.foodTypeIds ?? restaurantData.foodTypes ?? restaurantData.foodType,
  };
};

const mapRestaurantUpdatePayloadForApi = (restaurantData: any = {}) => {
  return {
    integrationSetup: restaurantData.integrationSetup ?? restaurantData.integration_setup,
    name: restaurantData.name,
    public_menu_enabled:
      restaurantData.public_menu_enabled ??
      restaurantData.publicMenuEnabled ??
      restaurantData.publicMenu ??
      restaurantData.public_menu,
    public_menu_key:
      restaurantData.public_menu_key ??
      restaurantData.publicMenuKey,
    description: restaurantData.description,
    logo: restaurantData.logo,
    banner: restaurantData.banner,
    cover_image: restaurantData.cover_image ?? restaurantData.coverImage,
    cuisineType: restaurantData.cuisineType ?? restaurantData.cuisine_type,
    address: restaurantData.address,
    cityId: restaurantData.cityId ?? restaurantData.city_id,
    stateId: restaurantData.stateId ?? restaurantData.state_id,
    countryId: restaurantData.countryId ?? restaurantData.country_id,
    city: restaurantData.city,
    state: restaurantData.state,
    pincode: restaurantData.pincode ?? restaurantData.zipCode,
    latitude: restaurantData.latitude,
    longitude: restaurantData.longitude,
    phone: restaurantData.phone,
    email: restaurantData.email,
    website: restaurantData.website,
    primary_color: restaurantData.primary_color ?? restaurantData.primaryColor,
    secondary_color: restaurantData.secondary_color ?? restaurantData.secondaryColor,
    open_time: restaurantData.open_time ?? restaurantData.openTime,
    close_time: restaurantData.close_time ?? restaurantData.closeTime,
    delivery_time: restaurantData.delivery_time ?? restaurantData.deliveryTime,
    minimum_order: restaurantData.minimum_order ?? restaurantData.minimumOrder,
    delivery_fee: restaurantData.delivery_fee ?? restaurantData.deliveryFee,
    free_delivery_above: restaurantData.free_delivery_above ?? restaurantData.freeDeliveryAbove,
    tax_rate: restaurantData.tax_rate ?? restaurantData.taxRate,
    packaging_charges: restaurantData.packaging_charges ?? restaurantData.packagingCharges,
    is_pure_veg: restaurantData.is_pure_veg ?? restaurantData.isPureVeg,
    amenities: restaurantData.amenities,
    is_active: restaurantData.is_active ?? restaurantData.isActive,
    is_open: restaurantData.is_open ?? restaurantData.isOpen,
    has_offers: restaurantData.has_offers ?? restaurantData.hasOffers,
    priceRangeId: restaurantData.priceRangeId ?? restaurantData.priceRange,
    foodTypeIds: restaurantData.foodTypeIds ?? restaurantData.foodTypes ?? restaurantData.foodType,
  };
};

const removeEmptyFields = (payload: Record<string, any>) =>
  Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined && value !== null)
  );

// API endpoints organized by resource
export const api = {
  // Authentication
  auth: {
    login: async (credentials: { email: string; password: string }) => {
      const response = await apiClient.post<{
        success: boolean;
        data: {
          user: any;
          token?: string;
          accessToken?: string;
          authToken?: string;
          refreshToken?: string;
          mustResetPassword?: boolean;
        };
      }>('/auth/login', credentials);

      if (response.success) {
        const token =
          response.data.token ||
          response.data.accessToken ||
          response.data.authToken ||
          '';

        if (!token) {
          // Prevent stale token from previous session causing role mismatch.
          removeToken();
          throw new Error('Login succeeded but no auth token was returned by API');
        }

        setToken(token);
        if (response.data.refreshToken) {
          setRefreshToken(response.data.refreshToken);
        }
      }
      
      return response;
    },

    googleLogin: async (payload: { idToken?: string; code?: string; role?: string }) => {
      const response = await apiClient.post<{
        success: boolean;
        data: {
          user: any;
          token?: string;
          accessToken?: string;
          authToken?: string;
          refreshToken?: string;
          mustResetPassword?: boolean;
        };
      }>('/auth/google', payload);

      if (response.success) {
        const token =
          response.data.token ||
          response.data.accessToken ||
          response.data.authToken ||
          '';

        if (!token) {
          removeToken();
          throw new Error('Google login succeeded but no auth token was returned by API');
        }

        setToken(token);
        if (response.data.refreshToken) {
          setRefreshToken(response.data.refreshToken);
        }
      }

      return response;
    },

    forgotPassword: async (payload: { email: string }) => {
      return apiClient.post<{ success: boolean; message: string }>('/auth/forgot-password', payload);
    },

    resetPassword: async (payload: {
      newPassword: string;
      confirmPassword: string;
      currentPassword?: string;
    }) => {
      return apiClient.post<{ success: boolean; message: string; data: { userId: string; isFirstLogin: boolean } }>(
        '/auth/reset-password',
        payload
      );
    },

    register: async (userData: {
      email: string;
      password: string;
      name: string;
      phone?: string;
      address?: string;
      role?: string;
    }) => {
      const response = await apiClient.post<{
        success: boolean;
        data: {
          id: string;
          email: string;
          name: string;
          phone?: string | null;
          address?: string | null;
          role: string;
          roleId?: string | null;
          restaurantId?: string | null;
          createdAt: string;
        };
      }>('/auth/register', userData);

      return response;
    },

    logout: () => {
      removeToken();
    },

    getCurrentUser: () => {
      return apiClient.get<{ success: boolean; data: any }>('/auth/profile');
    },

    updateProfile: (payload: { name?: string; phone?: string; avatar?: string }) => {
      return apiClient.put<{ success: boolean; message: string; data: any }>('/auth/profile', payload);
    },

    getSecuritySettings: () => {
      return apiClient.get<{
        success: boolean;
        data: {
          twoFactorEnabled: boolean;
          sessionTimeoutMinutes: number;
          updatedAt?: string | null;
        };
      }>('/auth/security/settings');
    },

    updateSecuritySettings: (payload: {
      twoFactorEnabled?: boolean;
      sessionTimeoutMinutes?: number;
    }) => {
      return apiClient.put<{
        success: boolean;
        data: {
          twoFactorEnabled: boolean;
          sessionTimeoutMinutes: number;
          updatedAt?: string | null;
        };
      }>('/auth/security/settings', payload);
    },

    getLoginHistory: (params?: { page?: number; limit?: number }) => {
      const query = cleanParams(params as Record<string, any>);
      return apiClient.get<{
        success: boolean;
        data: {
          items: Array<{
            id: string;
            userId: string | null;
            email: string | null;
            userName: string | null;
            role: string | null;
            status: 'SUCCESS' | 'FAILED' | string;
            ipAddress: string | null;
            userAgent: string | null;
            failureReason: string | null;
            loggedInAt: string;
          }>;
          pagination: {
            page: number;
            limit: number;
            total: number;
            totalPages: number;
          };
        };
      }>(`/auth/security/login-history${query ? `?${query}` : ''}`);
    },
  },

  // Users
  users: {
    getAll: async (params?: {
      role?: string;
      restaurant?: string;
      restaurantId?: string;
      page?: number;
      limit?: number;
      search?: string;
      isActive?: boolean;
    }) => {
      const cleanedParams = Object.fromEntries(
        Object.entries(params ?? {}).filter(([, value]) => value !== undefined && value !== null && value !== '')
      );
      const query = new URLSearchParams(cleanedParams as any).toString();
      const endpoint = query ? `/users?${query}` : '/users';

      const raw = await apiClient.get<any>(endpoint);
      const users = Array.isArray(raw?.data)
        ? raw.data
        : Array.isArray(raw?.data?.users)
          ? raw.data.users
          : [];
      const pagination = raw?.pagination || raw?.data?.pagination || {};

      return {
        ...raw,
        data: {
          users,
          pagination,
        },
      } as { success: boolean; data: { users: any[]; pagination: any } };
    },

    getById: (id: string) => {
      return apiClient.get<{ success: boolean; data: any }>(`/users/${id}`);
    },

    create: async (userData: any) => {
      return await apiClient.post<{ success: boolean; data: any }>('/users', userData);
    },

    update: (id: string, userData: any) => {
      return apiClient.put<{ success: boolean; data: any }>(`/users/${id}`, userData);
    },

    delete: (id: string) => {
      return apiClient.delete<{ success: boolean }>(`/users/${id}`);
    },

    getSavedLocations: () => {
      return apiClient.get<{
        success: boolean;
        data: Array<{
          id: string;
          label: string;
          addressType: 'HOME' | 'OFFICE' | 'OTHER';
          fullAddress: string;
          deliveryInstructions?: string | null;
          latitude?: number | null;
          longitude?: number | null;
          isDefault?: boolean;
        }>;
      }>('/users/saved-locations');
    },
  },

  deliveryApplications: {
    apply: (payload: {
      name: string;
      email: string;
      phone: string;
      vehicleType: string;
      vehicleNumber: string;
      licenseNumber?: string;
      drivingLicenseFile: string;
      drivingLicenseName?: string;
      deliveryArea: string;
      idProofFile: string;
      idProofName?: string;
      bankAccountName: string;
      bankName: string;
      branchName: string;
      bankAccountNumber: string;
      bankIfsc: string;
    }) => {
      return apiClient.post<{
        success: boolean;
        message: string;
        data: {
          id: string;
          name: string;
          email: string;
          phone: string;
          vehicleType: string;
          vehicleNumber?: string | null;
          licenseNumber?: string | null;
          drivingLicenseUrl?: string | null;
          drivingLicenseName?: string | null;
          deliveryArea?: string | null;
          idProofUrl?: string | null;
          idProofName?: string | null;
          bankAccountName?: string | null;
          bankName?: string | null;
          branchName?: string | null;
          bankAccountNumber?: string | null;
          bankIfsc?: string | null;
          status: 'PENDING' | 'APPROVED' | 'REJECTED';
          accountUserId?: string | null;
          reviewedAt?: string | null;
          activatedAt?: string | null;
          createdAt: string | null;
        } | null;
      }>('/delivery-boy-applications', payload);
    },

    getMine: () => {
      return apiClient.get<{
        success: boolean;
        data:
          | {
              id: string;
              name: string;
              email: string;
              phone: string;
              vehicleType: string;
              vehicleNumber?: string | null;
                licenseNumber?: string | null;
              drivingLicenseUrl?: string | null;
              drivingLicenseName?: string | null;
              deliveryArea?: string | null;
              idProofUrl?: string | null;
                idProofName?: string | null;
                bankAccountName?: string | null;
                bankName?: string | null;
                branchName?: string | null;
                bankAccountNumber?: string | null;
                bankIfsc?: string | null;
              status: 'PENDING' | 'APPROVED' | 'REJECTED';
              accountUserId?: string | null;
              reviewedAt?: string | null;
              activatedAt?: string | null;
              createdAt?: string | null;
              updatedAt?: string | null;
            }
          | null;
      }>('/delivery-boy-applications/me');
    },

    submitMine: (payload: {
      phone: string;
      vehicleType: string;
      vehicleNumber: string;
      licenseNumber?: string;
      drivingLicenseFile: string;
      drivingLicenseName?: string;
      deliveryArea: string;
        idProofFile: string;
        idProofName?: string;
        bankAccountName: string;
        bankName: string;
        branchName: string;
        bankAccountNumber: string;
        bankIfsc: string;
    }) => {
      return apiClient.post<{
        success: boolean;
        message: string;
        data: any;
      }>('/delivery-boy-applications/me', payload);
    },

    list: (params?: { page?: number; limit?: number; search?: string; status?: 'PENDING' | 'APPROVED' | 'REJECTED' }) => {
      const query = cleanParams(params as Record<string, any>);
      return apiClient.get<{
        success: boolean;
        data: {
          applications: Array<{
            id: string;
            name: string;
            email: string;
            phone: string;
            vehicleType: string;
            vehicleNumber?: string | null;
              licenseNumber?: string | null;
            drivingLicenseUrl?: string | null;
            drivingLicenseName?: string | null;
            deliveryArea?: string | null;
            idProofUrl?: string | null;
              idProofName?: string | null;
              bankAccountName?: string | null;
              bankName?: string | null;
              branchName?: string | null;
              bankAccountNumber?: string | null;
              bankIfsc?: string | null;
            status: 'PENDING' | 'APPROVED' | 'REJECTED';
            reviewNotes?: string | null;
            reviewedBy?: string | null;
            accountUserId?: string | null;
            reviewedAt?: string | null;
            activatedAt?: string | null;
            createdAt?: string | null;
            updatedAt?: string | null;
          }>;
          pagination: {
            page: number;
            limit: number;
            total: number;
            totalPages: number;
          };
        };
      }>(`/delivery-boy-applications${query ? `?${query}` : ''}`);
    },

    review: (id: string, payload: { status: 'APPROVED' | 'REJECTED'; reviewNotes?: string }) => {
      return apiClient.patch<{
        success: boolean;
        message: string;
        data: any;
      }>(`/delivery-boy-applications/${id}/review`, payload);
    },
  },

  // Master Data
  masterData: {
    cuisineTypes: {
      getAll: () => apiClient.get<{ success: boolean; data: any[] }>('/master-data/cuisine-types'),
      create: (data: any) => apiClient.post('/master-data/cuisine-types', data),
      update: (id: string, data: any) => apiClient.put(`/master-data/cuisine-types/${id}`, data),
      delete: (id: string) => apiClient.delete(`/master-data/cuisine-types/${id}`),
    },

    amenities: {
      getAll: () => apiClient.get<{ success: boolean; data: any[] }>('/master-data/amenities'),
      create: (data: any) => apiClient.post('/master-data/amenities', data),
      update: (id: string, data: any) => apiClient.put(`/master-data/amenities/${id}`, data),
      delete: (id: string) => apiClient.delete(`/master-data/amenities/${id}`),
    },

    dietaryPreferences: {
      getAll: async () => {
        try {
          return await apiClient.get<{ success: boolean; data: any[] }>('/master-data/dietary-preferences');
        } catch {
          return apiClient.get<{ success: boolean; data: any[] }>('/master-data/dietary-tags');
        }
      },
      create: (data: any) => apiClient.post('/master-data/dietary-preferences', data),
    },

    dietaryTags: {
      getAll: () => apiClient.get<{ success: boolean; data: any[] }>('/master-data/dietary-tags'),
      create: (data: any) => apiClient.post('/master-data/dietary-tags', data),
      update: (id: string, data: any) => apiClient.put(`/master-data/dietary-tags/${id}`, data),
      delete: (id: string) => apiClient.delete(`/master-data/dietary-tags/${id}`),
    },

    customerRatingFilters: {
      getAll: () => apiClient.get<{ success: boolean; data: any[] }>('/master-data/customer-rating-filters'),
    },

    foodTags: {
      getAll: () => apiClient.get<{ success: boolean; data: any[] }>('/master-data/food-tags'),
      create: (data: any) => apiClient.post('/master-data/food-tags', data),
    },

    foodCategories: {
      getAll: (params?: { isActive?: boolean; search?: string }) => {
        const query = cleanParams(params as Record<string, any>);
        return apiClient.get<{ success: boolean; data: any[] }>(
          `/master-data/food-categories${query ? `?${query}` : ''}`
        );
      },
    },

    spiceLevels: {
      getAll: () => apiClient.get<{ success: boolean; data: any[] }>('/master-data/spice-levels'),
      create: (data: any) => apiClient.post('/master-data/spice-levels', data),
    },

    paymentMethods: {
      getAll: () => apiClient.get<{ success: boolean; data: any[] }>('/master-data/payment-methods'),
      create: (data: any) => apiClient.post('/master-data/payment-methods', data),
    },

    orderStatuses: {
      getAll: () => apiClient.get<{ success: boolean; data: any[] }>('/master-data/order-statuses'),
      create: (data: any) => apiClient.post('/master-data/order-statuses', data),
    },

    bookingStatuses: {
      getAll: () => apiClient.get<{ success: boolean; data: any[] }>('/master-data/booking-statuses'),
      create: (data: any) => apiClient.post('/master-data/booking-statuses', data),
    },

    orderTypes: {
      getAll: () => apiClient.get<{ success: boolean; data: any[] }>('/master-data/order-types'),
      create: (data: any) => apiClient.post('/master-data/order-types', data),
    },

    discountTypes: {
      getAll: () => apiClient.get<{ success: boolean; data: any[] }>('/master-data/discount-types'),
      create: (data: any) => apiClient.post('/master-data/discount-types', data),
    },

    offerCategories: {
      getAll: (params?: { isActive?: boolean; search?: string }) => {
        const query = cleanParams(params as Record<string, any>);
        return apiClient.get<{ success: boolean; data: any[] }>(
          `/master-data/offer-categories${query ? `?${query}` : ''}`
        );
      },
      getLandingOffers: () =>
        apiClient.get<{
          success: boolean;
          data: Array<{
            id: string;
            code: string;
            title: string;
            description: string;
            discount: string;
            badge: string;
            ctaLabel: string;
            image?: string | null;
            accentColor: string;
            background: string;
            chipBackground: string;
            displayOrder: number;
          }>;
        }>('/master-data/offer-categories/landing'),
      create: (data: any) => apiClient.post('/master-data/offer-categories', data),
      update: (id: string, data: any) => apiClient.put(`/master-data/offer-categories/${id}`, data),
      delete: (id: string) => apiClient.delete(`/master-data/offer-categories/${id}`),
    },

    countries: {
      getAll: () => apiClient.get<{ success: boolean; data: any[] }>('/master-data/countries'),
      create: (data: any) => apiClient.post('/master-data/countries', data),
    },

    states: {
      getByCountry: (countryId: string) => 
        apiClient.get<{ success: boolean; data: any[] }>(`/master-data/countries/${countryId}/states`),
      create: (data: any) => apiClient.post('/master-data/states', data),
    },

    cities: {
      getByState: (stateId: string) => 
        apiClient.get<{ success: boolean; data: any[] }>(`/master-data/states/${stateId}/cities`),
      getAll: (params?: { search?: string; stateId?: string }) => {
        const query = new URLSearchParams(params as any).toString();
        return apiClient.get<{ success: boolean; data: any[] }>(`/master-data/cities?${query}`);
      },
      create: (data: any) => apiClient.post('/master-data/cities', data),
    },

    departments: {
      getAll: (params?: { isActive?: boolean; search?: string }) => {
        const query = cleanParams(params as Record<string, any>);
        return apiClient.get<{ success: boolean; data: any[] }>(
          `/master-data/departments${query ? `?${query}` : ''}`
        );
      },
      create: (data: any) => apiClient.post('/master-data/departments', data),
      update: (id: string, data: any) => apiClient.put(`/master-data/departments/${id}`, data),
      delete: (id: string) => apiClient.delete(`/master-data/departments/${id}`),
    },

    seatingPreferences: {
      getAll: (params?: { isActive?: boolean; search?: string; page?: number; limit?: number }) => {
        const query = cleanParams(params as Record<string, any>);
        return apiClient.get<{
          success: boolean;
          data: any[];
          pagination?: { page: number; limit: number; total: number; totalPages: number };
        }>(`/master-data/seating-preferences${query ? `?${query}` : ''}`);
      },
      getById: (id: string) =>
        apiClient.get<{ success: boolean; data: any }>(`/seating-preference/${id}`),
      create: (data: any) => apiClient.post(`/seating-preference`, data),
      update: (id: string, data: any) => apiClient.put(`/seating-preference/${id}`, data),
      delete: (id: string) => apiClient.delete(`/seating-preference/${id}`),
      getByRestaurant: (restaurantId: string, params?: { includeInactive?: boolean; includeUnavailable?: boolean }) => {
        const query = cleanParams(params as Record<string, any>);
        return apiClient.get<{ success: boolean; data: any[] }>(
          `/seating-preference/restaurants/${restaurantId}${query ? `?${query}` : ''}`
        );
      },
      assignToRestaurant: (
        restaurantId: string,
        seatingPreferences: Array<{ seatingPreferenceId: string; isAvailable: boolean }>
      ) =>
        apiClient.post<{ success: boolean; data: any[] }>(
          `/seating-preference/restaurants/${restaurantId}`,
          { seatingPreferences }
        ),
    },

    serviceTypes: {
      getAll: () => apiClient.get<{ success: boolean; data: any[] }>('/master/service-types'),
      create: (data: any) => apiClient.post('/master/service-types', data),
      update: (id: string, data: any) => apiClient.put(`/master/service-types/${id}`, data),
      delete: (id: string) => apiClient.delete(`/master/service-types/${id}`),
    },

    timezones: {
      getAll: () => apiClient.get<{ success: boolean; data: any[] }>('/master-data/timezones'),
      create: (data: any) => apiClient.post('/master-data/timezones', data),
    },

    currencies: {
      getAll: () => apiClient.get<{ success: boolean; data: any[] }>('/master-data/currencies'),
      create: (data: any) => apiClient.post('/master-data/currencies', data),
    },

    languages: {
      getAll: () => apiClient.get<{ success: boolean; data: any[] }>('/master-data/languages'),
      create: (data: any) => apiClient.post('/master-data/languages', data),
    },

    userRoles: {
      getAll: () => apiClient.get<{ success: boolean; data: any[] }>('/master-data/user-roles'),
      create: (data: any) => apiClient.post('/master-data/user-roles', data),
      update: (id: string, data: any) => apiClient.put(`/master-data/user-roles/${id}`, data),
      delete: (id: string) => apiClient.delete(`/master-data/user-roles/${id}`),
    },
  },

  seatingPreferences: {
    getAll: (params?: { isActive?: boolean; search?: string; page?: number; limit?: number }) => {
      const query = cleanParams(params as Record<string, any>);
      return apiClient.get<{
        success: boolean;
        data: any[];
        pagination?: { page: number; limit: number; total: number; totalPages: number };
      }>(`/seating-preference${query ? `?${query}` : ''}`);
    },
    getById: (id: string) =>
      apiClient.get<{ success: boolean; data: any }>(`/seating-preference/${id}`),
    create: (data: any) => apiClient.post('/seating-preference', data),
    update: (id: string, data: any) => apiClient.put(`/seating-preference/${id}`, data),
    delete: (id: string) => apiClient.delete(`/seating-preference/${id}`),
    getByRestaurant: (restaurantId: string, params?: { includeInactive?: boolean; includeUnavailable?: boolean }) => {
      const query = cleanParams(params as Record<string, any>);
      return apiClient.get<{ success: boolean; data: any[] }>(
        `/seating-preference/restaurants/${restaurantId}${query ? `?${query}` : ''}`
      );
    },
    assignToRestaurant: (
      restaurantId: string,
      seatingPreferences: Array<{ seatingPreferenceId: string; isAvailable: boolean }>
    ) =>
      apiClient.post<{ success: boolean; data: any[] }>(
        `/seating-preference/restaurants/${restaurantId}`,
        { seatingPreferences }
      ),
  },

  // Restaurants
  restaurants: {
    getAll: (params?: {
      city?: string;
      cityId?: string;
      city_id?: string;
      cuisine?: string;
      cuisineType?: string;
      status?: 'active' | 'inactive' | 'all';
      search?: string;
      page?: number;
      limit?: number;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
    }) => {
      const query = cleanParams(params as Record<string, any>);
      return apiClient.get<{ success: boolean; data: any[] }>(`/restaurants${query ? `?${query}` : ''}`);
    },

    getNearby: (params: {
      latitude: number;
      longitude: number;
      radiusKm?: number;
      limit?: number;
    }) => {
      const query = cleanParams(params as Record<string, any>);
      return apiClient.get<{ success: boolean; data: any[]; meta?: any }>(
        `/restaurants/nearby${query ? `?${query}` : ''}`
      );
    },

    getById: (id: string) => {
      return apiClient.get<{ success: boolean; data: any }>(`/restaurants/${id}`);
    },

    getBySlug: (slug: string) => {
      const safeSlug = encodeURIComponent(String(slug || '').trim().toLowerCase());
      return apiClient.get<{ success: boolean; data: any }>(`/restaurants/slug/${safeSlug}`);
    },

    getPublicMenuByKey: (key: string) => {
      const safeKey = encodeURIComponent(String(key || '').trim().toLowerCase());
      return apiClient.get<{ success: boolean; data: any }>(`/restaurants/public-key/${safeKey}`);
    },

    getByPublicKey: (key: string) => {
      const safeKey = encodeURIComponent(String(key || '').trim().toLowerCase());
      return apiClient.get<{ success: boolean; data: any }>(`/restaurants/by-public-key/${safeKey}`);
    },

    getDashboardSummary: (id: string) => {
      return apiClient.get<{
        success: boolean;
        data: {
          restaurant: any;
          metrics: {
            totalOrders: number;
            todayOrders: number;
            activeOrders: number;
            pendingOrders: number;
            todayRevenue: number;
            rating: number;
            totalReviews: number;
          };
          menuOverview: {
            totalItems: number;
            activeItems: number;
            categories: number;
          };
          orderStatusCounts: Record<string, number>;
          recentOrders: Array<{
            id: string;
            orderNumber: string;
            status: string;
            total: number;
            createdAt: string;
            customerName: string;
            itemsCount: number;
          }>;
        };
      }>(`/restaurants/${id}/dashboard-summary`);
    },

    getAnalytics: (id: string, params?: { range?: 'today' | 'week' | 'month' | 'year' }) => {
      const query = cleanParams(params as Record<string, any>);
      return apiClient.get<{
        success: boolean;
        data: {
          range: 'today' | 'week' | 'month' | 'year';
          period: {
            currentStart: string;
            currentEnd: string;
            previousStart: string;
            previousEnd: string;
          };
          metrics: {
            totalRevenue: number;
            totalOrders: number;
            averageOrderValue: number;
            uniqueCustomers: number;
            repeatRate: number;
            completionRate: number;
            activeMenuItems: number;
            revenueGrowth: number;
            orderGrowth: number;
            averageRating: number;
            totalReviews: number;
          };
          charts: {
            revenueTrend: Array<{ name: string; revenue: number; orders: number }>;
            orderStatusDistribution: Array<{ key: string; name: string; value: number; color: string }>;
            peakHours: Array<{ hour: string; orders: number }>;
            topSellingItems: Array<{ id: string; name: string; quantity: number; revenue: number }>;
          };
        };
      }>(`/restaurants/${id}/analytics${query ? `?${query}` : ''}`);
    },

    getLocationOptions: (params?: { countryId?: string; stateId?: string }) => {
      const query = cleanParams(params as Record<string, any>);
      return apiClient.get<{
        success: boolean;
        data: {
          countries: Array<{ id: string; name: string; code?: string }>;
          states: Array<{ id: string; name: string; countryId?: string }>;
          cities: Array<{ id: string; name: string; stateId?: string; countryId?: string }>;
          priceRanges: Array<{
            id: string;
            code: string;
            name: string;
            symbol?: string | null;
            description?: string | null;
            displayOrder?: number;
          }>;
          foodTypes: Array<{
            id: string;
            code: string;
            name: string;
          }>;
        };
      }>(`/restaurants/location-options${query ? `?${query}` : ''}`);
    },

    create: (restaurantData: any) => {
      const payload = removeEmptyFields(mapRestaurantPayloadForApi(restaurantData));
      return apiClient.post<{ success: boolean; data: any }>('/restaurants', payload);
    },

    update: (id: string, restaurantData: any) => {
      const payload = removeEmptyFields(mapRestaurantUpdatePayloadForApi(restaurantData));
      return apiClient.put<{ success: boolean; data: any }>(`/restaurants/${id}`, payload);
    },

    uploadImage: (payload: { imageData: string; fileName?: string }) => {
      return apiClient.post<{ success: boolean; data: { fileName: string; filePath: string; url: string } }>(
        '/restaurants/upload-image',
        payload
      );
    },

    uploadMediaFile: (payload: { fileData: string; fileName?: string }) => {
      return apiClient.post<{
        success: boolean;
        data: { fileName: string; filePath: string; url: string; mimeType: string; fileSize: number };
      }>('/restaurants/upload-media-file', payload);
    },

    getMedia: (restaurantId: string) => {
      return apiClient.get<{
        success: boolean;
        data: { photos: any[]; videos: any[]; items: any[] };
      }>(`/restaurants/${restaurantId}/media`);
    },

    addMedia: (
      restaurantId: string,
      payload:
        | {
            mediaType: 'PHOTO' | 'VIDEO';
            mediaUrl: string;
            thumbnailUrl?: string;
            fileName?: string;
            mimeType?: string;
            fileSize?: number;
            durationSeconds?: number;
            displayOrder?: number;
          }
        | {
            items: Array<{
              mediaType: 'PHOTO' | 'VIDEO';
              mediaUrl: string;
              thumbnailUrl?: string;
              fileName?: string;
              mimeType?: string;
              fileSize?: number;
              durationSeconds?: number;
              displayOrder?: number;
            }>;
          }
    ) => {
      return apiClient.post<{ success: boolean; data: any[] }>(`/restaurants/${restaurantId}/media`, payload);
    },

    deleteMedia: (restaurantId: string, mediaId: string) => {
      return apiClient.delete<{ success: boolean; message: string }>(`/restaurants/${restaurantId}/media/${mediaId}`);
    },

    delete: (id: string) => {
      return apiClient.delete<{ success: boolean }>(`/restaurants/${id}`);
    },

    getStatus: () => {
      return apiClient.get<{
        success: boolean;
        data: { is_open: 0 | 1; last_updated: string | null };
      }>('/restaurant/status');
    },

    updateStatus: (payload: { is_open: 0 | 1 }) => {
      return apiClient.patch<{
        success: boolean;
        data: { is_open: 0 | 1; last_updated: string | null };
      }>('/restaurant/status', payload);
    },
  },

  restaurantCustomizations: {
    getAll: (restaurantId: string) => {
      return apiClient.get<{ success: boolean; data: any[] }>(`/restaurants/${restaurantId}/customizations`);
    },

    create: (restaurantId: string, payload: any) => {
      return apiClient.post<{ success: boolean; data: any }>(`/restaurants/${restaurantId}/customizations`, payload);
    },

    update: (restaurantId: string, groupId: string, payload: any) => {
      return apiClient.put<{ success: boolean; data: any }>(
        `/restaurants/${restaurantId}/customizations/${groupId}`,
        payload
      );
    },

    delete: (restaurantId: string, groupId: string) => {
      return apiClient.delete<{ success: boolean; message: string }>(
        `/restaurants/${restaurantId}/customizations/${groupId}`
      );
    },
  },

  modifierGroups: {
    create: (payload: {
      restaurant_id?: string;
      restaurantId?: string;
      group_name: string;
      system_name: string;
      input_type: 'single_select' | 'multi_select';
      min_select: number;
      max_select: number | null;
      is_required: boolean;
      sort_order: number;
      options: Array<{
        option_name: string;
        price: number;
        is_default: boolean;
        sort_order?: number;
      }>;
    }) => {
      return apiClient.post<{ success: boolean; message: string; data?: any }>(`/modifier-groups`, payload);
    },
  },

  // Menu
  menu: {
    getCategories: async (restaurantId: string) => {
      try {
        return await apiClient.get<{ success: boolean; data: any[] }>(
          `/menu/restaurants/${restaurantId}/categories`
        );
      } catch {
        return apiClient.get<{ success: boolean; data: any[] }>(
          `/categories/restaurant/${restaurantId}`
        );
      }
    },

    getItems: async (
      restaurantId: string,
      categoryId?: string,
      options?: { isActive?: 'true' | 'false' | 'all' } | boolean
    ) => {
      const resolvedIsActive =
        typeof options === 'boolean' ? (options ? 'all' : undefined) : options?.isActive;
      const query = cleanParams({ categoryId, isActive: resolvedIsActive });
      const modernPath = `/menu/restaurant/${restaurantId}${query ? `?${query}` : ''}`;
      const legacyPath = `/menu/restaurants/${restaurantId}/items${query ? `?${query}` : ''}`;
      try {
        return await apiClient.get<{ success: boolean; data: any[] }>(modernPath);
      } catch {
        return apiClient.get<{ success: boolean; data: any[] }>(legacyPath);
      }
    },

    getItemById: async (id: string) => {
      return apiClient.get<{ success: boolean; data: any }>(`/menu/${id}`);
    },

    createCategory: async (categoryData: any) => {
      try {
        return await apiClient.post<{ success: boolean; data: any }>('/categories', categoryData);
      } catch {
        return apiClient.post<{ success: boolean; data: any }>('/menu/categories', categoryData);
      }
    },

    createItem: async (itemData: any) => {
      try {
        return await apiClient.post<{ success: boolean; data: any }>('/menu', itemData);
      } catch {
        return apiClient.post<{ success: boolean; data: any }>('/menu/items', itemData);
      }
    },

    updateItem: async (id: string, itemData: any) => {
      try {
        return await apiClient.put<{ success: boolean; data: any }>(`/menu/${id}`, itemData);
      } catch {
        return apiClient.put<{ success: boolean; data: any }>(`/menu/items/${id}`, itemData);
      }
    },

    deleteItem: async (id: string) => {
      try {
        return await apiClient.delete<{ success: boolean }>(`/menu/${id}`);
      } catch {
        return apiClient.delete<{ success: boolean }>(`/menu/items/${id}`);
      }
    },
  },

  // Orders
  orders: {
    getAll: (params?: { restaurantId?: string; customerId?: string; status?: string }) => {
      const query = cleanParams(params as Record<string, any>);
      return apiClient.get<{
        success: boolean;
        data:
          | any[]
          | {
              orders: any[];
              pagination?: {
                total: number;
                page: number;
                limit: number;
                totalPages: number;
              };
            };
      }>(`/orders${query ? `?${query}` : ''}`);
    },

    getById: (id: string) => {
      return apiClient.get<{ success: boolean; data: any }>(`/orders/${id}`);
    },

    create: (orderData: any) => {
      return apiClient.post<{ success: boolean; data: any }>('/orders', orderData);
    },

    getDeliveryQueue: (params?: { latitude?: number; longitude?: number }) => {
      const query = cleanParams(params as Record<string, any>);
      return apiClient.get<{ success: boolean; data: any[]; meta?: any }>(
        `/orders/delivery/queue${query ? `?${query}` : ''}`
      );
    },

    getMyOrders: (params?: { page?: number; limit?: number }) => {
      const query = cleanParams(params as Record<string, any>);
      return apiClient.get<{
        success: boolean;
        data: {
          orders: any[];
          pagination?: {
            total: number;
            page: number;
            limit: number;
            totalPages: number;
          };
        };
      }>(`/orders/my-orders${query ? `?${query}` : ''}`);
    },

    updateStatus: (id: string, statusCode: string, notes?: string) => {
      return apiClient.patch<{ success: boolean; data: any }>(`/orders/${id}/status`, {
        status: statusCode,
        statusCode,
        notes,
      });
    },

    acceptDelivery: (id: string) => {
      return apiClient.patch<{ success: boolean; message: string }>(`/orders/${id}/delivery/accept`);
    },

    rejectDelivery: (id: string) => {
      return apiClient.patch<{ success: boolean; message: string }>(`/orders/${id}/delivery/reject`);
    },

    confirmPickup: (id: string) => {
      return apiClient.patch<{ success: boolean; message: string }>(`/orders/${id}/delivery/pickup`);
    },

    confirmDelivery: (id: string) => {
      return apiClient.patch<{ success: boolean; message: string }>(`/orders/${id}/delivery/complete`);
    },

    cancel: (id: string, reason: string) => {
      return apiClient.post<{ success: boolean; data: any }>(`/orders/${id}/cancel`, {
        reason,
      });
    },
  },

  // Delivery Tracking
  deliveryTracking: {
    getByOrder: (orderId: string) => {
      return apiClient.get<{ success: boolean; data: any }>(`/delivery-tracking/${orderId}`);
    },
    updateLocation: (payload: {
      orderId: string;
      latitude: number;
      longitude: number;
      heading?: number;
      speed?: number;
      partnerId?: string;
      partnerName?: string;
      partnerPhone?: string;
      partnerPhoto?: string;
      vehicleType?: string;
    }) => {
      return apiClient.post<{ success: boolean; data: any }>(`/delivery-tracking/update`, payload);
    },
  },

  // Bookings
  bookings: {
    getAll: (params?: { restaurantId?: string; customerId?: string }) => {
      const query = cleanParams(params as Record<string, any>);
      return apiClient.get<{ success: boolean; data: any[] }>(`/bookings${query ? `?${query}` : ''}`);
    },

    getById: (id: string) => {
      return apiClient.get<{ success: boolean; data: any }>(`/bookings/${id}`);
    },

    create: (bookingData: any) => {
      return apiClient.post<{ success: boolean; data: any }>('/bookings', bookingData);
    },

    updateStatus: (id: string, statusCode: string) => {
      return apiClient.patch<{ success: boolean; data: any }>(`/bookings/${id}/status`, {
        statusCode,
      });
    },
  },

  // Reviews
  reviews: {
    getByRestaurant: async (restaurantId: string) => {
      try {
        return await apiClient.get<{ success: boolean; data: any[] }>(
          `/reviews/restaurants/${restaurantId}`
        );
      } catch {
        return apiClient.get<{ success: boolean; data: any[] }>(
          `/reviews/restaurant/${restaurantId}`
        );
      }
    },

    create: (reviewData: any) => {
      return apiClient.post<{ success: boolean; data: any }>('/reviews', reviewData);
    },

    addResponse: (id: string, response: string) => {
      return apiClient.post<{ success: boolean; data: any }>(`/reviews/${id}/response`, {
        response,
      });
    },
  },

  // Coupons
  coupons: {
    getAll: (restaurantId?: string) => {
      const query = restaurantId ? `?restaurantId=${restaurantId}` : '';
      return apiClient.get<{ success: boolean; data: any[] }>(`/coupons${query}`);
    },

    getByRestaurant: async (restaurantId: string, includeInactive = false) => {
      // Keep restaurant-scoped route clean: don't send `isActive=false` when we want all.
      // Some backends treat missing `isActive` as include-all on `/restaurant/:id` but not on `/coupons`.
      const query = includeInactive ? '' : cleanParams({ isActive: true });
      const path = `/coupons/restaurant/${restaurantId}${query ? `?${query}` : ''}`;

      try {
        return await apiClient.get<{ success: boolean; data: any[] }>(path);
      } catch {
        const fallbackQuery = includeInactive ? 'isActive=false' : 'isActive=true';
        return apiClient.get<{ success: boolean; data: any[] }>(
          `/coupons?restaurantId=${encodeURIComponent(restaurantId)}&${fallbackQuery}`
        );
      }
    },

    create: (couponData: any) => {
      return apiClient.post<{ success: boolean; data: any }>('/coupons', couponData);
    },

    validate: (code: string, restaurantId: string, subtotal: number) => {
      return apiClient.post<{ success: boolean; data: any }>('/coupons/validate', {
        code,
        restaurantId,
        orderValue: subtotal,
        subtotal,
      });
    },

    update: (id: string, couponData: any) => {
      return apiClient.put<{ success: boolean; data: any }>(`/coupons/${id}`, couponData);
    },

    delete: (id: string) => {
      return apiClient.delete<{ success: boolean }>(`/coupons/${id}`);
    },
  },

  // Offers (Restaurant Offers master)
  offers: {
    getAll: (restaurantId?: string) => {
      const query = restaurantId ? `?restaurantId=${restaurantId}` : '';
      return apiClient.get<{ success: boolean; data: any[] }>(`/offers${query}`);
    },

    getByRestaurant: async (restaurantId: string, includeInactive = false) => {
      // Keep restaurant-scoped route clean: don't send `isActive=false` when we want all.
      // Some backends treat missing `isActive` as include-all on `/restaurant/:id` but not on `/offers`.
      const query = includeInactive ? '' : cleanParams({ isActive: true });
      const path = `/offers/restaurant/${restaurantId}${query ? `?${query}` : ''}`;

      try {
        return await apiClient.get<{ success: boolean; data: any[] }>(path);
      } catch {
        const fallbackQuery = includeInactive ? 'isActive=false' : 'isActive=true';
        return apiClient.get<{ success: boolean; data: any[] }>(
          `/offers?restaurantId=${encodeURIComponent(restaurantId)}&${fallbackQuery}`
        );
      }
    },

    create: (offerData: any) => {
      return apiClient.post<{ success: boolean; data: any }>('/offers', offerData);
    },

    update: (id: string, offerData: any) => {
      return apiClient.put<{ success: boolean; data: any }>(`/offers/${id}`, offerData);
    },

    delete: (id: string) => {
      return apiClient.delete<{ success: boolean }>(`/offers/${id}`);
    },
  },
};

// Health check
export const checkBackendHealth = async (): Promise<boolean> => {
  try {
    const base = API_BASE_URL.replace(/\/api(\/v1)?$/, '');
    const response = await fetch(`${base}/health`, {
      method: 'GET',
      signal: AbortSignal.timeout(2000),
    });

    if (!response.ok) return false;

    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) return true;

    const data = await response.json().catch(() => null);
    if (!data) return true;

    if (typeof (data as any).success === 'boolean') return Boolean((data as any).success);
    if (String((data as any).status || '').trim().toUpperCase() === 'OK') return true;
    if (String((data as any).database || '').trim().toLowerCase() === 'connected') return true;

    return true;
  } catch (error) {
    // Silent fail - backend not available
    return false;
  }
};

// Export token management functions
export { getToken, setToken, removeToken };

export default api;

// Named exports for backward compatibility
export const authAPI = api.auth;
export const userAPI = api.users;
export const deliveryApplicationAPI = api.deliveryApplications;
export const restaurantAPI = api.restaurants;
export const menuAPI = api.menu;
export const orderAPI = api.orders;
export const bookingAPI = api.bookings;
export const reviewAPI = api.reviews;
export const couponAPI = api.coupons;
export const offerAPI = api.offers;

